import React, { useState, useEffect } from 'react';
import { Shield, Building2, FileCheck, Clock, CheckCircle, XCircle, AlertTriangle, Eye, Settings, Trophy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import DashboardLayout from '@/components/dashboard/DashboardLayout';
import StatCard from '@/components/dashboard/StatCard';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '@/services/api';

const DashboardMasterAdmin = () => {
  const { empresa } = useAuth();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [empresasPendentes, setEmpresasPendentes] = useState([]);
  const [auditoria, setAuditoria] = useState([]);
  const [loading, setLoading] = useState(false);
  
  const [openDetalhes, setOpenDetalhes] = useState(false);
  const [empresaSelecionada, setEmpresaSelecionada] = useState(null);
  const [detalhesEmpresa, setDetalhesEmpresa] = useState(null);
  
  const [openAvaliar, setOpenAvaliar] = useState(false);
  const [acaoAvaliar, setAcaoAvaliar] = useState(null); // 'aprovar' | 'rejeitar'
  const [motivoRejeicao, setMotivoRejeicao] = useState('');

  useEffect(() => {
    carregarDados();
  }, []);

  const carregarDados = async () => {
    setLoading(true);
    try {
      const [dashRes, pendentesRes, auditoriaRes] = await Promise.all([
        api.get('/master/dashboard'),
        api.get('/master/validacao/empresas-pendentes'),
        api.get('/master/auditoria?limit=20')
      ]);
      
      setDashboard(dashRes.data);
      setEmpresasPendentes(pendentesRes.data);
      setAuditoria(auditoriaRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const abrirDetalhes = async (empresa) => {
    setEmpresaSelecionada(empresa);
    setOpenDetalhes(true);
    
    try {
      const response = await api.get(`/master/validacao/empresa/${empresa.id}`);
      setDetalhesEmpresa(response.data);
    } catch (error) {
      toast.error('Erro ao carregar detalhes');
    }
  };

  const handleAvaliarEmpresa = async () => {
    if (!empresaSelecionada) return;
    
    setLoading(true);
    try {
      const status = acaoAvaliar === 'aprovar' ? 'aprovado' : 'rejeitado';
      
      await api.post('/master/validacao/avaliar-empresa', {
        empresa_id: empresaSelecionada.id,
        status_validacao: status,
        motivo: acaoAvaliar === 'rejeitar' ? motivoRejeicao : null
      });
      
      toast.success(`Empresa ${acaoAvaliar === 'aprovar' ? 'aprovada' : 'rejeitada'} com sucesso!`);
      
      setOpenAvaliar(false);
      setOpenDetalhes(false);
      setMotivoRejeicao('');
      carregarDados();
    } catch (error) {
      toast.error('Erro ao avaliar empresa');
    } finally {
      setLoading(false);
    }
  };

  const handleReabrirDocumentos = async (empresa) => {
    if (!empresa) return;
    if (!window.confirm(`Reabrir envio de documentos para "${empresa.razao_social}"? A empresa volta para "cadastro_iniciado" e poderá enviar documentos novamente.`)) return;
    const limpar = window.confirm('Apagar os documentos antigos da empresa? (OK = apagar / Cancelar = manter histórico)');
    setLoading(true);
    try {
      const token = localStorage.getItem('rota017_token');
      await api.post(`/admin/empresas/${empresa.id}/reabrir-documentos`,
        { motivo: 'Solicitação administrativa', limpar_documentos_antigos: limpar },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Cadastro reaberto. Empresa pode enviar documentos.');
      setOpenDetalhes(false);
      carregarDados();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao reabrir documentos');
    } finally {
      setLoading(false);
    }
  };

  const handleAnexarDocAdmin = async (empresa) => {
    if (!empresa) return;
    const tipo = window.prompt(
      'Tipo do documento (cartao_cnpj, contrato_social, documento_socio, cnh, documento_pessoal):',
      'cartao_cnpj'
    );
    if (!tipo) return;
    const url = window.prompt('URL do arquivo (link público do documento):');
    if (!url) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('rota017_token');
      await api.post(`/admin/empresas/${empresa.id}/documentos`,
        { empresa_id: empresa.id, tipo: tipo.trim(), nome_arquivo: tipo + '.pdf', url: url.trim(),
          observacao: 'Anexado manualmente pelo admin' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Documento anexado pelo admin');
      carregarDados();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao anexar documento');
    } finally {
      setLoading(false);
    }
  };

  const handleAvaliarDocumento = async (docId, status) => {
    setLoading(true);
    try {
      await api.post('/master/validacao/avaliar-documento', {
        documento_id: docId,
        status: status,
        motivo_rejeicao: status === 'rejeitado' ? 'Documento ilegível ou inválido' : null
      });
      
      toast.success(`Documento ${status === 'aprovado' ? 'aprovado' : 'rejeitado'}!`);
      
      // Recarrega detalhes
      const response = await api.get(`/master/validacao/empresa/${empresaSelecionada.id}`);
      setDetalhesEmpresa(response.data);
    } catch (error) {
      toast.error('Erro ao avaliar documento');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      cadastro_iniciado: { label: 'Iniciado', color: 'bg-slate-100 text-slate-800' },
      documentos_enviados: { label: 'Docs Enviados', color: 'bg-blue-100 text-blue-800' },
      em_analise: { label: 'Em Análise', color: 'bg-amber-100 text-amber-800' },
      aprovado: { label: 'Aprovado', color: 'bg-green-100 text-green-800' },
      rejeitado: { label: 'Rejeitado', color: 'bg-red-100 text-red-800' },
      bloqueado: { label: 'Bloqueado', color: 'bg-red-200 text-red-900' }
    };
    const badge = badges[status] || badges.cadastro_iniciado;
    return <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold ${badge.color}`}>{badge.label}</span>;
  };

  const getDocStatusBadge = (status) => {
    const badges = {
      pendente: { label: 'Pendente', color: 'bg-amber-100 text-amber-800' },
      aprovado: { label: 'Aprovado', color: 'bg-green-100 text-green-800' },
      rejeitado: { label: 'Rejeitado', color: 'bg-red-100 text-red-800' }
    };
    const badge = badges[status] || badges.pendente;
    return <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${badge.color}`}>{badge.label}</span>;
  };

  return (
    <DashboardLayout title="Master Admin - Validação Cadastral">
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        <StatCard
          title="Total Empresas"
          value={dashboard?.total_empresas || 0}
          icon={Building2}
          color="blue"
        />
        <StatCard
          title="Pendentes"
          value={dashboard?.cadastros_pendentes || 0}
          icon={Clock}
          color="amber"
        />
        <StatCard
          title="Aprovadas"
          value={dashboard?.empresas_aprovadas || 0}
          icon={CheckCircle}
          color="emerald"
        />
        <StatCard
          title="Rejeitadas"
          value={dashboard?.empresas_rejeitadas || 0}
          icon={XCircle}
          color="red"
        />
        <StatCard
          title="Docs Pendentes"
          value={dashboard?.documentos_pendentes || 0}
          icon={FileCheck}
          color="purple"
        />
      </div>

      {/* Navegação Rápida - Configurações e Ranking */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <button
          onClick={() => navigate('/admin/configuracoes')}
          className="bg-gradient-to-br from-orange-600 to-amber-500 text-white rounded-2xl p-6 text-left hover:shadow-lg transition-shadow"
          data-testid="btn-nav-configuracoes"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <Settings className="w-6 h-6" />
            </div>
            <div>
              <h3 className="font-bold text-lg">Configurações da Plataforma</h3>
              <p className="text-sm text-orange-100">Tempo de liberação, raio GPS e pesos do ranking</p>
            </div>
          </div>
        </button>

        <button
          onClick={() => navigate('/admin/ranking')}
          className="bg-gradient-to-br from-amber-500 to-orange-500 text-white rounded-2xl p-6 text-left hover:shadow-lg transition-shadow"
          data-testid="btn-nav-ranking"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <Trophy className="w-6 h-6" />
            </div>
            <div>
              <h3 className="font-bold text-lg">Ranking de Entregadores</h3>
              <p className="text-sm text-amber-50">Classificação e avaliações administrativas</p>
            </div>
          </div>
        </button>
      </div>

      {/* Empresas Pendentes */}
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden mb-8">
        <div className="p-6 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Empresas Aguardando Validação
          </h2>
          <Button variant="outline" onClick={carregarDados} disabled={loading}>
            Atualizar
          </Button>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase">Empresa</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase">CNPJ</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase">Tipo</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase">Data</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {empresasPendentes.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center text-slate-500">
                    Nenhuma empresa pendente de validação
                  </td>
                </tr>
              ) : (
                empresasPendentes.map((emp) => (
                  <tr key={emp.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium text-slate-900">{emp.nome_fantasia || emp.razao_social}</p>
                        <p className="text-xs text-slate-500">{emp.razao_social}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm font-mono text-slate-600">{emp.cnpj}</td>
                    <td className="px-6 py-4">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${emp.tipo_empresa === 'cliente' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'}`}>
                        {emp.tipo_empresa}
                      </span>
                    </td>
                    <td className="px-6 py-4">{getStatusBadge(emp.status_validacao)}</td>
                    <td className="px-6 py-4 text-sm text-slate-600">
                      {new Date(emp.criado_em).toLocaleDateString('pt-BR')}
                    </td>
                    <td className="px-6 py-4">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => abrirDetalhes(emp)}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Analisar
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Log de Auditoria */}
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <div className="p-6 border-b border-slate-200">
          <h2 className="text-xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Log de Auditoria
          </h2>
        </div>
        
        <div className="p-4 space-y-2 max-h-96 overflow-y-auto">
          {auditoria.length === 0 ? (
            <p className="text-center text-slate-500 py-4">Nenhuma ação registrada</p>
          ) : (
            auditoria.map((log, idx) => (
              <div key={idx} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${log.acao.includes('aprovou') ? 'bg-green-100' : 'bg-red-100'}`}>
                  {log.acao.includes('aprovou') ? <CheckCircle className="w-4 h-4 text-green-600" /> : <XCircle className="w-4 h-4 text-red-600" />}
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-slate-900">{log.acao.replace(/_/g, ' ')}</p>
                  <p className="text-xs text-slate-500">{log.detalhe}</p>
                </div>
                <p className="text-xs text-slate-400">
                  {new Date(log.data_hora).toLocaleString('pt-BR')}
                </p>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Modal Detalhes */}
      <Dialog open={openDetalhes} onOpenChange={setOpenDetalhes}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Análise de Cadastro</DialogTitle>
          </DialogHeader>
          
          {detalhesEmpresa && (
            <div className="space-y-6">
              {/* Dados da Empresa */}
              <div className="bg-slate-50 rounded-lg p-4">
                <h3 className="font-semibold text-slate-900 mb-3">Dados da Empresa</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <label className="text-slate-500">Razão Social</label>
                    <p className="font-medium">{detalhesEmpresa.empresa.razao_social}</p>
                  </div>
                  <div>
                    <label className="text-slate-500">Nome Fantasia</label>
                    <p className="font-medium">{detalhesEmpresa.empresa.nome_fantasia || '-'}</p>
                  </div>
                  <div>
                    <label className="text-slate-500">CNPJ</label>
                    <p className="font-mono">{detalhesEmpresa.empresa.cnpj}</p>
                  </div>
                  <div>
                    <label className="text-slate-500">Tipo</label>
                    <p className="font-medium capitalize">{detalhesEmpresa.empresa.tipo_empresa}</p>
                  </div>
                  <div>
                    <label className="text-slate-500">E-mail</label>
                    <p>{detalhesEmpresa.empresa.email}</p>
                  </div>
                  <div>
                    <label className="text-slate-500">Telefone</label>
                    <p className="font-mono">{detalhesEmpresa.empresa.telefone}</p>
                  </div>
                </div>
              </div>

              {/* Documentos */}
              <div>
                <h3 className="font-semibold text-slate-900 mb-3">Documentos Enviados</h3>
                
                {!detalhesEmpresa.documentos_check?.completo && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-amber-600" />
                      <p className="text-sm text-amber-800 font-medium">Documentos faltantes:</p>
                    </div>
                    <p className="text-sm text-amber-700 mt-1">
                      {detalhesEmpresa.documentos_check?.faltantes?.join(', ') || 'Nenhum documento enviado'}
                    </p>
                  </div>
                )}
                
                {detalhesEmpresa.documentos?.length > 0 ? (
                  <div className="space-y-2">
                    {detalhesEmpresa.documentos.map((doc) => (
                      <div key={doc.id} className="flex items-center justify-between p-3 border rounded-lg">
                        <div>
                          <p className="font-medium text-sm">{doc.tipo_documento.replace(/_/g, ' ')}</p>
                          <p className="text-xs text-slate-500">{doc.nome_arquivo}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          {getDocStatusBadge(doc.status)}
                          {doc.status === 'pendente' && (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-green-600 border-green-600"
                                onClick={() => handleAvaliarDocumento(doc.id, 'aprovado')}
                                disabled={loading}
                              >
                                <CheckCircle className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600 border-red-600"
                                onClick={() => handleAvaliarDocumento(doc.id, 'rejeitado')}
                                disabled={loading}
                              >
                                <XCircle className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-sm">Nenhum documento enviado</p>
                )}
              </div>

              {/* Contrato */}
              {detalhesEmpresa.contrato && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h3 className="font-semibold text-green-900 mb-2">Contrato Aceito</h3>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <label className="text-green-700">Tipo</label>
                      <p className="font-medium">{detalhesEmpresa.contrato.tipo_contrato}</p>
                    </div>
                    <div>
                      <label className="text-green-700">Data</label>
                      <p>{new Date(detalhesEmpresa.contrato.data_assinatura).toLocaleString('pt-BR')}</p>
                    </div>
                    <div>
                      <label className="text-green-700">IP</label>
                      <p className="font-mono">{detalhesEmpresa.contrato.ip_assinatura}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Ações */}
              <div className="flex gap-3 pt-4 border-t flex-wrap">
                <Button
                  className="flex-1 bg-green-600 hover:bg-green-700 min-w-[140px]"
                  onClick={() => {
                    setAcaoAvaliar('aprovar');
                    setOpenAvaliar(true);
                  }}
                  disabled={loading}
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Aprovar Cadastro
                </Button>
                <Button
                  variant="outline"
                  className="flex-1 border-red-600 text-red-600 hover:bg-red-50 min-w-[140px]"
                  onClick={() => {
                    setAcaoAvaliar('rejeitar');
                    setOpenAvaliar(true);
                  }}
                  disabled={loading}
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Rejeitar
                </Button>
              </div>
              
              {/* Ações secundárias (sempre visíveis) */}
              <div className="flex gap-2 flex-wrap">
                <Button
                  variant="outline"
                  size="sm"
                  className="border-amber-500 text-amber-700 hover:bg-amber-50 flex-1 min-w-[160px]"
                  onClick={() => handleReabrirDocumentos(empresaSelecionada)}
                  disabled={loading}
                  data-testid="btn-reabrir-docs"
                >
                  Reabrir envio de docs
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="border-blue-500 text-blue-700 hover:bg-blue-50 flex-1 min-w-[160px]"
                  onClick={() => handleAnexarDocAdmin(empresaSelecionada)}
                  disabled={loading}
                  data-testid="btn-anexar-doc-admin"
                >
                  Anexar doc manualmente
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal Confirmar Avaliação */}
      <Dialog open={openAvaliar} onOpenChange={setOpenAvaliar}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {acaoAvaliar === 'aprovar' ? 'Confirmar Aprovação' : 'Confirmar Rejeição'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {acaoAvaliar === 'aprovar' ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-sm text-green-800">
                  Ao aprovar, a empresa poderá operar na plataforma após ativar a assinatura.
                </p>
              </div>
            ) : (
              <>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-sm text-red-800">
                    A empresa será notificada sobre a rejeição e precisará corrigir as pendências.
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Motivo da Rejeição *</label>
                  <Textarea
                    value={motivoRejeicao}
                    onChange={(e) => setMotivoRejeicao(e.target.value)}
                    placeholder="Descreva o motivo da rejeição..."
                    required
                  />
                </div>
              </>
            )}
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setOpenAvaliar(false)}
              >
                Cancelar
              </Button>
              <Button
                className={`flex-1 ${acaoAvaliar === 'aprovar' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`}
                onClick={handleAvaliarEmpresa}
                disabled={loading || (acaoAvaliar === 'rejeitar' && !motivoRejeicao)}
              >
                {loading ? 'Processando...' : 'Confirmar'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default DashboardMasterAdmin;
